// context/combinedContext.jsx
import { createContext, useCallback, useEffect, useState } from "react";
import { getInitialLanguage } from "./allStates/initialLanguage";
import {
	SESSION_EXPIRED_EVENT,
	TOKENS_UPDATED_EVENT,
	clearPendingExpiry,
	endSession,
	getPendingExpiry,
	getStoredTokens,
	revalidateIfStale,
	watchCrossTabAuth,
} from "../auth/tokenManager";

export const CombinedContext = createContext();

/* Belt and braces for tabs left open for hours: the visibility handler is the
   one that really matters, since browsers throttle timers in background tabs. */
const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000;

export const CombinedContextProvider = ({ children, initialUserData, isLoadingUser = false }) => {
	const [selectedService, setSelectedService] = useState(null);
	const [selectedstaff, setSelectedstaff] = useState(null);
	const [userData, setUserData] = useState(initialUserData || null);
	const [language, setLanguage] = useState(getInitialLanguage());
	const [category, setCategory] = useState(null);
	const [shopsData, setShopsData] = useState(null);
	/* A session that died during boot expired before this provider existed to
	   hear the event, so pick that up on the way in. */
	const [sessionExpired, setSessionExpired] = useState(() => Boolean(getPendingExpiry()));

	useEffect(() => {
		if (language) {
			localStorage.setItem("lang", language);
		}
	}, [language]);

	/* Mirror refreshed tokens into userData so components that key off
	   cognitoTokens keep working — and refetch when they depend on it. */
	useEffect(() => {
		const onTokensUpdated = (event) => {
			const tokens = event.detail || getStoredTokens();
			setUserData((prev) =>
				prev
					? {
							...prev,
							cognitoTokens: {
								IdToken: tokens.idToken,
								AccessToken: tokens.accessToken,
								RefreshToken: tokens.refreshToken,
							},
					  }
					: prev
			);
		};

		window.addEventListener(TOKENS_UPDATED_EVENT, onTokensUpdated);
		return () => window.removeEventListener(TOKENS_UPDATED_EVENT, onTokensUpdated);
	}, []);

	/* The session is genuinely over. Dropping userData is enough — ProtectedRoute
	   sees a guest and sends protected pages to sign-in on its own. */
	useEffect(() => {
		const onSessionExpired = () => {
			setUserData(null);
			setSessionExpired(true);
		};

		window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
		return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
	}, []);

	/* Keep the access token warm while the app is open, and top it up the moment
	   the user comes back to a tab that has been sitting idle. */
	useEffect(() => {
		const stopCrossTabWatch = watchCrossTabAuth();

		const revalidate = () => {
			if (document.visibilityState === "hidden") return;
			revalidateIfStale();
		};

		document.addEventListener("visibilitychange", revalidate);
		window.addEventListener("focus", revalidate);
		window.addEventListener("online", revalidate);
		const timer = setInterval(revalidate, REVALIDATE_INTERVAL_MS);

		return () => {
			stopCrossTabWatch();
			document.removeEventListener("visibilitychange", revalidate);
			window.removeEventListener("focus", revalidate);
			window.removeEventListener("online", revalidate);
			clearInterval(timer);
		};
	}, []);

	const dismissSessionExpired = useCallback(() => {
		clearPendingExpiry();
		setSessionExpired(false);
	}, []);

	const logout = useCallback(() => {
		endSession("user-signed-out");
		setUserData(null);
		dismissSessionExpired();
	}, [dismissSessionExpired]);

	return (
		<CombinedContext.Provider
			value={{
				selectedService,
				setSelectedService,
				selectedstaff,
				setSelectedstaff,
				userData,
				setUserData,
				language,
				setLanguage,
				category,
				setCategory,
				shopsData,
				setShopsData,
				isLoadingUser,
				sessionExpired,
				clearSessionExpired: dismissSessionExpired,
				logout,
			}}
		>
			{children}
		</CombinedContext.Provider>
	);
};
